/**
 * RideroDailyFeedWidget - Extracted from HubDashboard.jsx
 * Shows detailed order feed with live updates
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Briefcase, Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RideroDailyFeedWidget() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await axios.get(`${API}/ridero/orders`, { withCredentials: true });
        setOrders(res.data.orders || []);
      } catch (err) {
        console.error('Failed to fetch Ridero orders:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'in_progress': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'accepted': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getServiceIcon = (service) => {
    switch(service?.toLowerCase()) {
      case 'delivery': return '📦';
      case 'parts': return '🔧';
      case 'hotel': return '🏨';
      case 'airport': return '✈️';
      case 'ride': return '🚗';
      default: return '🚚';
    }
  };

  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full" data-testid="ridero-daily-feed-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#e94560]/20 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-[#e94560]" />
          </div>
          <h3 className="font-bold">Ridero Daily Feed</h3>
        </div>
        <span className="text-xs bg-[#e94560]/20 text-[#e94560] px-2.5 py-1 rounded-full font-medium animate-pulse">
          LIVE • {orders.length} orders
        </span>
      </div>
      
      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-24 bg-secondary/40 rounded-xl"></div>
          <div className="h-24 bg-secondary/40 rounded-xl"></div>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No orders yet</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
          {orders.slice(0, 10).map((order) => (
            <div key={order.order_id} className="p-3 rounded-xl bg-secondary/30 border border-border/30 hover:bg-secondary/50 transition-colors">
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getServiceIcon(order.service)}</span>
                  <span className="font-medium text-sm">{order.order_id}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${getStatusColor(order.status)}`}>
                  {order.status?.toUpperCase()}
                </span>
              </div>
              
              {/* Service Type */}
              <div className="text-xs text-accent font-medium mb-2 uppercase">
                {order.service || 'Delivery'}
              </div>
              
              {/* Items/Parts */}
              {order.items && order.items.length > 0 && (
                <div className="mb-2 p-2 rounded-lg bg-secondary/50">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="text-sm">
                      <span className="font-medium">{item.name}</span>
                      {item.quantity > 1 && <span className="text-muted-foreground"> x{item.quantity}</span>}
                      {item.notes && <p className="text-xs text-muted-foreground mt-0.5">{item.notes}</p>}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Notes */}
              {order.notes && (
                <p className="text-xs text-muted-foreground mb-2 italic">"{order.notes}"</p>
              )}
              
              {/* Pickup & Dropoff */}
              <div className="text-xs space-y-1">
                <div className="flex items-start gap-2">
                  <span className="text-green-400">↑</span>
                  <span className="text-muted-foreground truncate">{order.pickup?.address || 'N/A'}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-red-400">↓</span>
                  <span className="text-muted-foreground truncate">{order.dropoff?.address || 'N/A'}</span>
                </div>
              </div>
              
              {/* Driver & Customer */}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/30 text-xs">
                <div className="flex items-center gap-1">
                  <Truck className="w-3 h-3 text-blue-400" />
                  <span>{order.driver?.name || 'Unassigned'}</span>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <span>{order.customer?.name || 'Customer'}</span>
                </div>
              </div>
              
              {/* Time */}
              <div className="text-xs text-muted-foreground mt-1">
                {order.created_at && new Date(order.created_at).toLocaleString()}
                {order.estimated_delivery && <span className="ml-2">• ETA: {order.estimated_delivery}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
